document.documentElement.classList.remove('no-js');

const revealObserver = new IntersectionObserver((entries) => entries.forEach((entry) => {
  if (entry.isIntersecting) entry.target.classList.add('visible');
}), { threshold: .08 });
document.querySelectorAll('.reveal').forEach((el) => revealObserver.observe(el));

// V28 progressive compact-on-scroll header. Smoothly compresses instead of snapping at a threshold.
(() => {
  const headerGroup = document.querySelector('.shopify-section-group-header-group');
  if (!headerGroup) return;
  let ticking = false;
  const update = () => {
    const progress = Math.min(Math.max(window.scrollY / 150, 0), 1);
    headerGroup.style.setProperty('--header-progress', progress.toFixed(3));
    headerGroup.classList.toggle('is-scrolled', window.scrollY > 8);
    ticking = false;
  };
  window.addEventListener('scroll', () => {
    if (!ticking) { requestAnimationFrame(update); ticking = true; }
  }, { passive: true });
  update();
})();

const moneyFallback = (value) => value;

// V36 — keep the header bag badge in sync after AJAX cart changes.
const refreshBagCount = async () => {
  try {
    const response = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    document.querySelectorAll('.bag-count').forEach((badge) => {
      badge.textContent = String(cart.item_count || 0);
      badge.setAttribute('aria-label', `${cart.item_count || 0} items in bag`);
    });
  } catch (e) {}
};

const normalizeColourToken = (value = '') => String(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const colourAliases = (value = '') => {
  const token = normalizeColourToken(value);
  const aliases = new Set([token, token.replace(/ /g, '-'), token.replace(/ /g, '')]);
  if (token === 'ice') ['white','pearl white','pearl'].forEach(v => aliases.add(v));
  if (token === 'sky') ['sky blue','blue'].forEach(v => aliases.add(v));
  if (token === 'pastel purple') ['purple','lilac'].forEach(v => aliases.add(v));
  if (token === 'onyx cheetah') ['onyx','cheetah','black cheetah'].forEach(v => aliases.add(v));
  if (token === 'leopard') ['cheetah','animal print'].forEach(v => aliases.add(v));
  return [...aliases].filter(Boolean);
};

// V30 explicit colour-to-photo mappings supplied during product QA.
// Photo numbers are 1-based and refer to the image order shown in Shopify's product gallery.
const explicitPhotoNumber = (productTitle = '', colour = '') => {
  const t = normalizeColourToken(productTitle);
  const c0 = normalizeColourToken(colour);
  let c = c0 === 'gray' ? 'grey' : c0;
  if (c0.includes('ice')) c = 'ice';
  else if (c0.includes('sky')) c = 'sky';
  else if (c0.includes('black')) c = 'black';
  else if (c0.includes('burgundy')) c = 'burgundy';
  else if (c0.includes('steel')) c = 'steel';
  else if (c0.includes('pastel purple')) c = 'pastel purple';
  else if (c0.includes('pearl white')) c = 'pearl white';
  else if (c0.includes('light grey') || c0.includes('light gray')) c = 'light grey';
  else if (c0.includes('grey') || c0.includes('gray')) c = 'grey';
  else if (c0.includes('navy')) c = 'navy';
  else if (c0.includes('khaki')) c = 'khaki';
  else if (c0.includes('military')) c = 'military';
  else if (c0.includes('olive')) c = 'olive';
  else if (c0.includes('pewter')) c = 'pewter';
  else if (c0.includes('graphite')) c = 'graphite';
  else if (c0.includes('berry')) c = 'berry';
  else if (c0.includes('smoke')) c = 'smoke';
  else if (c0.includes('white')) c = 'white';
  const rules = [
    [/vector panel jogger/, { ice:4, sky:6 }],
    [/axis tech jogger/, { navy:10, khaki:7, grey:4 }],
    [/motion shorts?/, { grey:3, olive:2 }],
    [/utility liner short/, { military:3 }],
    [/(ford|forge) short/, { military:3 }],
    [/form long ?line tee/, { black:7, khaki:1, military:1 }],
    [/(macht|mac?t) wild leggings?/, { leopard:5, 'onyx cheetah':1 }],
    [/core tank/, { white:5, grey:3 }],
    [/apex performance t/, { black:10, white:7, navy:4, grey:1 }],
    [/command performance t/, { black:4, white:7, burgundy:1 }],
    [/(vira|vyra) set/, { 'pastel purple':6, smoke:11, military:17, steel:21 }],
    [/(vira|vyra) leggings?/, { black:5, 'pastel purple':9, smoke:1, military:15, steel:18 }],
    [/(vira|vyra) sports bra/, { black:3, 'pastel purple':6, grey:9, olive:12, steel:1 }],
    [/motion set/, { 'light grey':11, pewter:6 }],
    [/motion leggings?/, { black:5, 'light grey':1, pewter:11 }],
    [/motion sports bra/, { black:4, 'light grey':6, pewter:1 }],
    [/sculpt seamless set/, { graphite:9, 'pearl white':4 }],
    [/sculpt leggings?/, { berry:5, graphite:8, 'pearl white':2 }],
    [/sculpt sports bra/, { berry:6, graphite:1, 'pearl white':2 }]
  ];
  for (const [pattern, map] of rules) {
    if (!pattern.test(t)) continue;
    if (map[c] != null) return map[c];
    if (c === 'light gray' && map['light grey'] != null) return map['light grey'];
  }
  return null;
};
const textMatchesColour = (text, colour) => {
  const hay = normalizeColourToken(text);
  return colourAliases(colour).some(alias => hay.includes(normalizeColourToken(alias)));
};

// Collection cards: colour + size selection, variant-aware availability and quick Add to Bag.
document.querySelectorAll('[data-product-card]').forEach((card) => {
  const json = card.querySelector('[data-card-variants]');
  if (!json) return;
  let variants = [];
  try { variants = JSON.parse(json.textContent); } catch (e) { return; }
  if (!variants.length) return;

  const optionButtons = [...card.querySelectorAll('[data-card-option-index]')];
  const addButton = card.querySelector('[data-card-add]');
  const status = card.querySelector('[data-card-status]');
  const image = card.querySelector('[data-card-image]');
  const price = card.querySelector('[data-card-price]');
  const selected = [...variants[0].options];
  const firstAvailable = variants.find((v) => v.available) || variants[0];
  firstAvailable.options.forEach((value, index) => { selected[index] = value; });

  const findVariant = () => variants.find((variant) => variant.options.every((value, index) => value === selected[index]));

  const refresh = (switchImage = false) => {
    const variant = findVariant();
    optionButtons.forEach((button) => {
      const index = Number(button.dataset.cardOptionIndex);
      const value = button.dataset.cardOptionValue;
      button.classList.toggle('is-selected', selected[index] === value);
      const colourIndex = Number(card.dataset.colorOptionIndex);
      const isColour = Number.isFinite(colourIndex) && index === colourIndex;
      const possible = isColour
        ? variants.some((v) => v.available && v.options[index] === value)
        : variants.some((v) => v.available && v.options[index] === value && (!Number.isFinite(colourIndex) || colourIndex < 0 || v.options[colourIndex] === selected[colourIndex]));
      button.classList.toggle('is-unavailable', !possible);
      button.disabled = !possible;
      button.setAttribute('aria-disabled', String(!possible));
    });
    if (variant && price) price.textContent = moneyFallback(variant.price);
    if (addButton) {
      addButton.disabled = !variant || !variant.available;
      addButton.textContent = variant && variant.available ? 'Add to bag' : 'Unavailable';
    }
    if (switchImage && variant && image) {
      const colourIndex = Number(card.dataset.colorOptionIndex);
      const colourValue = Number.isFinite(colourIndex) && colourIndex >= 0 ? selected[colourIndex] : '';
      const explicitNumber = explicitPhotoNumber(card.dataset.productTitle || '', colourValue);
      const explicitImage = explicitNumber ? cardImages[explicitNumber - 1] : null;
      const fallback = cardImages.find((entry) => textMatchesColour(`${entry.alt || ''} ${entry.url || ''}`, colourValue));
      const colourButtons = [...card.querySelectorAll('.colour-swatch')];
      const activeColour = colourButtons.find((button) => button.dataset.cardOptionValue === colourValue);
      const colourOrdinal = Number(activeColour?.dataset.colourOrdinal || 0);
      const groupedFallback = cardImages.length ? cardImages[Math.min(cardImages.length - 1, Math.floor((colourOrdinal * cardImages.length) / Math.max(1, colourButtons.length)))] : null;
      const nextSrc = explicitImage?.url || variant.image || fallback?.url || groupedFallback?.url;
      if (nextSrc) {
        image.classList.add('is-switching');
        const temp = new Image();
        temp.onload = () => { image.src = nextSrc; cardImageIndex = Math.max(0, cardImages.findIndex((entry) => entry.url === nextSrc)); image.classList.remove('is-switching'); };
        temp.onerror = () => image.classList.remove('is-switching');
        temp.src = nextSrc;
      }
    }
    return variant;
  };

  optionButtons.forEach((button) => button.addEventListener('click', () => {
    if (button.disabled) return;
    const index = Number(button.dataset.cardOptionIndex);
    selected[index] = button.dataset.cardOptionValue;
    const colourIndex = Number(card.dataset.colorOptionIndex);
    const isColour = Number.isFinite(colourIndex) && index === colourIndex;
    let variant = findVariant();
    if (isColour && (!variant || !variant.available)) {
      const firstInColour = variants.find((v) => v.available && v.options[colourIndex] === selected[colourIndex]);
      if (firstInColour) firstInColour.options.forEach((value, i) => { if (i !== colourIndex) selected[i] = value; });
    } else if (!variant || !variant.available) {
      const compatible = variants.find((v) => v.available && v.options[index] === selected[index] && (!Number.isFinite(colourIndex) || colourIndex < 0 || v.options[colourIndex] === selected[colourIndex]));
      if (compatible) compatible.options.forEach((value, i) => { if (i !== colourIndex) selected[i] = value; });
    }
    refresh(true);
  }));

  if (addButton) addButton.addEventListener('click', async () => {
    const variant = refresh(false);
    if (!variant || !variant.available) return;
    addButton.disabled = true;
    addButton.textContent = 'Adding…';
    if (status) status.textContent = '';
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
      });
      if (!response.ok) throw new Error('Unable to add this item.');
      await refreshBagCount();
      addButton.textContent = 'Added';
      if (status) status.textContent = 'Added to your bag.';
      window.setTimeout(() => { addButton.disabled = false; addButton.textContent = 'Add to bag'; }, 1200);
    } catch (error) {
      addButton.disabled = false; addButton.textContent = 'Add to bag';
      if (status) status.textContent = 'Please try again.';
    }
  });

  const imagesJson = card.querySelector('[data-card-images]');
  let cardImages = []; try { cardImages = JSON.parse(imagesJson?.textContent || '[]'); } catch(e) {}
  let cardImageIndex = Math.max(0, cardImages.findIndex((entry) => entry.url === image?.src));
  const showCardImage = (delta) => {
    if (!image || cardImages.length < 2) return;
    cardImageIndex = (cardImageIndex + delta + cardImages.length) % cardImages.length;
    image.classList.add('is-switching');
    image.src = cardImages[cardImageIndex].url;
    window.setTimeout(() => image.classList.remove('is-switching'), 180);
  };
  const cardPrev = card.querySelector('[data-card-image-prev]');
  const cardNext = card.querySelector('[data-card-image-next]');
  cardPrev?.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); showCardImage(-1); });
  cardNext?.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); showCardImage(1); });
  [cardPrev, cardNext].forEach((control, idx) => control?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); showCardImage(idx === 0 ? -1 : 1); }
  }));

  refresh(true);
});

// Product page: premium variant controls, colour-linked gallery, carousel, hover zoom and fast checkout.
(() => {
  const root = document.querySelector('[data-product-root]');
  if (!root) return;
  const json = root.querySelector('[data-product-variants]');
  if (!json) return;
  let variants = [];
  try { variants = JSON.parse(json.textContent); } catch (e) { return; }
  if (!variants.length) return;

  const optionButtons = [...root.querySelectorAll('[data-product-option]')];
  const variantInput = root.querySelector('[data-variant-id]');
  const price = root.querySelector('[data-product-price]');
  const addButton = root.querySelector('[data-add-to-bag]');
  const buyButton = root.querySelector('[data-buy-now]');
  const status = root.querySelector('[data-pdp-status]');
  const mobilePrice = document.querySelector('[data-mobile-price]');
  const mobileAdd = document.querySelector('[data-mobile-add]');
  const mobileBuy = document.querySelector('[data-mobile-buy]');
  const slides = [...root.querySelectorAll('.gallery-slide')];
  const thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
  const prev = root.querySelector('[data-gallery-prev]');
  const next = root.querySelector('[data-gallery-next]');
  const currentCounter = root.querySelector('[data-gallery-current]');

  const initiallySelected = variants.find((v) => String(v.id) === String(variantInput?.value)) || variants.find((v) => v.available) || variants[0];
  const selected = [...initiallySelected.options];
  let activeIndex = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));

  const showSlide = (index) => {
    if (!slides.length) return;
    activeIndex = (index + slides.length) % slides.length;
    slides.forEach((slide, i) => slide.classList.toggle('is-active', i === activeIndex));
    const id = slides[activeIndex].dataset.mediaId;
    thumbs.forEach((thumb) => thumb.classList.toggle('is-active', thumb.dataset.mediaId === id));
    if (currentCounter) currentCounter.textContent = String(activeIndex + 1);
  };
  const showMedia = (mediaId) => {
    if (!mediaId) return;
    const index = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
    if (index >= 0) showSlide(index);
  };
  prev?.addEventListener('click', () => showSlide(activeIndex - 1));
  next?.addEventListener('click', () => showSlide(activeIndex + 1));
  thumbs.forEach((thumb) => thumb.addEventListener('click', () => {
    const index = slides.findIndex((slide) => slide.dataset.mediaId === thumb.dataset.mediaId);
    if (index >= 0) showSlide(index);
  }));
  const thumbsRail = root.querySelector('[data-gallery-thumbs]');
  const thumbPrev = root.querySelector('[data-thumb-prev]');
  const thumbNext = root.querySelector('[data-thumb-next]');
  const scrollThumbs = (direction) => thumbsRail?.scrollBy({ left: direction * Math.max(280, thumbsRail.clientWidth * .78), behavior: 'smooth' });
  thumbPrev?.addEventListener('click', () => scrollThumbs(-1));
  thumbNext?.addEventListener('click', () => scrollThumbs(1));

  // Mobile swipe.
  const stage = root.querySelector('[data-gallery-stage]');
  let touchStart = 0;
  stage?.addEventListener('touchstart', (e) => { touchStart = e.changedTouches[0].clientX; }, { passive: true });
  stage?.addEventListener('touchend', (e) => {
    const delta = e.changedTouches[0].clientX - touchStart;
    if (Math.abs(delta) > 48) showSlide(activeIndex + (delta < 0 ? 1 : -1));
  }, { passive: true });

  // V37 — unified premium product lightbox for mobile + desktop.
  // Mobile: tap, pinch and drag. Desktop: click, wheel/trackpad zoom, drag and gallery navigation.
  const lightbox = document.createElement('div');
  lightbox.className = 'product-lightbox';
  lightbox.setAttribute('aria-hidden', 'true');
  lightbox.innerHTML = `
    <button type="button" class="product-lightbox-close" aria-label="Close image zoom">×</button>
    <button type="button" class="product-lightbox-nav product-lightbox-prev" aria-label="Previous product image">‹</button>
    <div class="product-lightbox-viewport"><img class="product-lightbox-image" alt=""></div>
    <button type="button" class="product-lightbox-nav product-lightbox-next" aria-label="Next product image">›</button>
    <p class="product-lightbox-hint"><span class="lightbox-hint-mobile">Pinch to zoom · drag to move</span><span class="lightbox-hint-desktop">Scroll to zoom · drag to move</span></p>`;
  document.body.appendChild(lightbox);
  const lightboxImage = lightbox.querySelector('.product-lightbox-image');
  const lightboxViewport = lightbox.querySelector('.product-lightbox-viewport');
  const lightboxClose = lightbox.querySelector('.product-lightbox-close');
  const lightboxPrev = lightbox.querySelector('.product-lightbox-prev');
  const lightboxNext = lightbox.querySelector('.product-lightbox-next');
  let lightboxIndex = activeIndex;
  let zoomScale = 1, zoomX = 0, zoomY = 0, pinchStartDistance = 0, pinchStartScale = 1;
  let dragStartX = 0, dragStartY = 0, dragOriginX = 0, dragOriginY = 0, mouseDragging = false;

  const applyZoom = () => {
    lightboxImage.style.transform = `translate3d(${zoomX}px,${zoomY}px,0) scale(${zoomScale})`;
    lightboxViewport.classList.toggle('is-zoomed', zoomScale > 1.02);
  };
  const resetZoom = () => { zoomScale = 1; zoomX = 0; zoomY = 0; applyZoom(); };
  const setLightboxImage = (index) => {
    if (!slides.length) return;
    lightboxIndex = (index + slides.length) % slides.length;
    const slide = slides[lightboxIndex];
    const img = slide.querySelector('.gallery-image');
    if (!img) return;
    lightboxImage.src = slide.dataset.mediaSrc || img.currentSrc || img.src;
    lightboxImage.alt = img.alt || root.dataset.productTitle || 'Product image';
    resetZoom();
    showSlide(lightboxIndex);
  };
  const openLightbox = (index) => {
    setLightboxImage(index);
    lightbox.classList.add('is-open');
    lightbox.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('lightbox-open');
    lightboxClose.focus({ preventScroll: true });
  };
  const closeLightbox = () => {
    lightbox.classList.remove('is-open');
    lightbox.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('lightbox-open');
    mouseDragging = false;
    resetZoom();
  };
  const moveLightbox = (direction) => setLightboxImage(lightboxIndex + direction);

  root.querySelectorAll('[data-zoom-wrap]').forEach((wrap) => {
    wrap.addEventListener('click', (e) => {
      const slide = wrap.closest('.gallery-slide');
      const index = slides.indexOf(slide);
      if (index < 0) return;
      e.preventDefault();
      openLightbox(index);
    });
  });
  lightboxClose.addEventListener('click', closeLightbox);
  lightboxPrev.addEventListener('click', (e) => { e.stopPropagation(); moveLightbox(-1); });
  lightboxNext.addEventListener('click', (e) => { e.stopPropagation(); moveLightbox(1); });
  lightbox.addEventListener('click', (e) => { if (e.target === lightbox) closeLightbox(); });
  document.addEventListener('keydown', (e) => {
    if (!lightbox.classList.contains('is-open')) return;
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') moveLightbox(-1);
    if (e.key === 'ArrowRight') moveLightbox(1);
    if ((e.key === '+' || e.key === '=') && zoomScale < 4) { zoomScale = Math.min(4, zoomScale + .35); applyZoom(); }
    if (e.key === '-' && zoomScale > 1) { zoomScale = Math.max(1, zoomScale - .35); if (zoomScale === 1) { zoomX = 0; zoomY = 0; } applyZoom(); }
  });

  // Desktop wheel / trackpad zoom centred on pointer position.
  lightboxViewport.addEventListener('wheel', (e) => {
    if (window.innerWidth <= 900) return;
    e.preventDefault();
    const oldScale = zoomScale;
    const change = e.deltaY < 0 ? .2 : -.2;
    zoomScale = Math.min(4, Math.max(1, zoomScale + change));
    if (zoomScale === 1) { zoomX = 0; zoomY = 0; }
    else if (oldScale !== zoomScale) {
      const rect = lightboxViewport.getBoundingClientRect();
      const px = e.clientX - (rect.left + rect.width / 2);
      const py = e.clientY - (rect.top + rect.height / 2);
      const ratio = zoomScale / oldScale;
      zoomX = px - (px - zoomX) * ratio;
      zoomY = py - (py - zoomY) * ratio;
    }
    applyZoom();
  }, { passive: false });

  // Desktop click-drag when zoomed.
  lightboxViewport.addEventListener('mousedown', (e) => {
    if (window.innerWidth <= 900 || zoomScale <= 1) return;
    e.preventDefault();
    mouseDragging = true;
    dragStartX = e.clientX; dragStartY = e.clientY; dragOriginX = zoomX; dragOriginY = zoomY;
    lightboxViewport.classList.add('is-dragging');
  });
  window.addEventListener('mousemove', (e) => {
    if (!mouseDragging) return;
    zoomX = dragOriginX + e.clientX - dragStartX;
    zoomY = dragOriginY + e.clientY - dragStartY;
    applyZoom();
  });
  window.addEventListener('mouseup', () => { mouseDragging = false; lightboxViewport.classList.remove('is-dragging'); });

  // Mobile pinch zoom and one-finger pan once enlarged.
  const distance = (a,b) => Math.hypot(a.clientX-b.clientX, a.clientY-b.clientY);
  lightboxViewport.addEventListener('touchstart', (e) => {
    if (e.touches.length === 2) { pinchStartDistance = distance(e.touches[0], e.touches[1]); pinchStartScale = zoomScale; }
    else if (e.touches.length === 1 && zoomScale > 1) { dragStartX = e.touches[0].clientX; dragStartY = e.touches[0].clientY; dragOriginX = zoomX; dragOriginY = zoomY; }
  }, { passive: true });
  lightboxViewport.addEventListener('touchmove', (e) => {
    if (e.touches.length === 2 && pinchStartDistance) {
      e.preventDefault();
      zoomScale = Math.min(4, Math.max(1, pinchStartScale * distance(e.touches[0], e.touches[1]) / pinchStartDistance));
      if (zoomScale === 1) { zoomX = 0; zoomY = 0; }
      applyZoom();
    } else if (e.touches.length === 1 && zoomScale > 1) {
      e.preventDefault();
      zoomX = dragOriginX + e.touches[0].clientX - dragStartX;
      zoomY = dragOriginY + e.touches[0].clientY - dragStartY;
      applyZoom();
    }
  }, { passive: false });
  lightboxViewport.addEventListener('touchend', (e) => { if (e.touches.length < 2) pinchStartDistance = 0; if (zoomScale <= 1.02) resetZoom(); }, { passive: true });
  lightboxViewport.addEventListener('dblclick', () => { zoomScale = zoomScale > 1 ? 1 : 2; zoomX = 0; zoomY = 0; applyZoom(); });

  const findVariant = () => variants.find((v) => v.options.every((value, index) => value === selected[index]));
  const update = (moveMedia = false) => {
    let variant = findVariant();
    if (!variant) variant = variants.find((v) => v.available && v.options.every((value, index) => !selected[index] || value === selected[index]));
    optionButtons.forEach((button) => {
      const index = Number(button.dataset.optionIndex);
      const value = button.dataset.optionValue;
      button.classList.toggle('is-selected', selected[index] === value);
      const colourIndex = Number(root.dataset.colorOptionIndex);
      const isColour = Number.isFinite(colourIndex) && index === colourIndex;
      const possible = isColour
        ? variants.some((v) => v.available && v.options[index] === value)
        : variants.some((v) => v.available && v.options[index] === value && (!Number.isFinite(colourIndex) || colourIndex < 0 || v.options[colourIndex] === selected[colourIndex]));
      button.classList.toggle('is-unavailable', !possible);
      button.disabled = !possible;
      button.setAttribute('aria-disabled', String(!possible));
      if (button.classList.contains('pdp-size-chip')) {
        button.textContent = possible ? value : `${value} — Sold out`;
      }
    });
    root.querySelectorAll('.pdp-option').forEach((fieldset) => {
      const i = Number(fieldset.dataset.optionIndex);
      const label = fieldset.querySelector('[data-selected-label]');
      if (label) label.textContent = selected[i] || '';
    });
    if (variant) {
      if (variantInput) variantInput.value = variant.id;
      if (price) price.textContent = variant.price;
      if (mobilePrice) mobilePrice.textContent = variant.price;
      [addButton, buyButton, mobileAdd, mobileBuy].forEach((button) => { if (button) button.disabled = !variant.available; });
      if (addButton) addButton.textContent = variant.available ? 'Add to bag' : 'Sold out';
      if (mobileAdd) mobileAdd.textContent = variant.available ? 'Add to bag' : 'Sold out';
      if (status) {
        const qty = Number(variant.inventoryQuantity);
        status.textContent = variant.available && Number.isFinite(qty) && qty > 0 && qty <= 3 ? `Only ${qty} left in this size.` : '';
      }
      if (moveMedia) {
        const colourIndex = Number(root.dataset.colorOptionIndex);
        const colourValue = Number.isFinite(colourIndex) && colourIndex >= 0 ? selected[colourIndex] : '';
        const explicitNumber = explicitPhotoNumber(root.dataset.productTitle || '', colourValue);
        if (explicitNumber && slides[explicitNumber - 1]) {
          showSlide(explicitNumber - 1);
        } else if (variant.mediaId) {
          showMedia(variant.mediaId);
        } else {
          const matchingSlide = slides.find((slide) => textMatchesColour(`${slide.dataset.mediaAlt || ''} ${slide.dataset.mediaSrc || ''}`, colourValue));
          if (matchingSlide) {
            showMedia(matchingSlide.dataset.mediaId);
          } else {
            const colourButtons = [...root.querySelectorAll('.pdp-swatches .colour-swatch')];
            const activeColour = colourButtons.find((button) => button.dataset.optionValue === colourValue);
            const colourOrdinal = Number(activeColour?.dataset.colourOrdinal || 0);
            const groupedIndex = Math.min(slides.length - 1, Math.floor((colourOrdinal * slides.length) / Math.max(1, colourButtons.length)));
            if (slides[groupedIndex]) showSlide(groupedIndex);
          }
        }
      }
    }
    return variant;
  };

  optionButtons.forEach((button) => button.addEventListener('click', () => {
    if (button.disabled) return;
    const index = Number(button.dataset.optionIndex);
    selected[index] = button.dataset.optionValue;
    const colourIndex = Number(root.dataset.colorOptionIndex);
    const isColour = Number.isFinite(colourIndex) && index === colourIndex;
    let variant = findVariant();
    if (isColour && (!variant || !variant.available)) {
      const firstInColour = variants.find((v) => v.available && v.options[colourIndex] === selected[colourIndex]);
      if (firstInColour) firstInColour.options.forEach((value, i) => { if (i !== colourIndex) selected[i] = value; });
    } else if (!variant || !variant.available) {
      const compatible = variants.find((v) => v.available && v.options[index] === selected[index] && (!Number.isFinite(colourIndex) || colourIndex < 0 || v.options[colourIndex] === selected[colourIndex]));
      if (compatible) compatible.options.forEach((value, i) => { if (i !== colourIndex) selected[i] = value; });
    }
    update(true);
  }));

  const addVariant = async (goCheckout = false) => {
    const variant = update(false);
    if (!variant || !variant.available) {
      if (status) status.textContent = 'Please choose an available size and colour.';
      return;
    }
    [addButton, buyButton, mobileAdd, mobileBuy].forEach((button) => { if (button) button.disabled = true; });
    if (status) status.textContent = goCheckout ? 'Taking you to checkout…' : 'Adding to bag…';
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
      });
      if (!response.ok) throw new Error('Cart error');
      await refreshBagCount();
      if (goCheckout) { window.location.assign('/checkout'); return; }
      if (status) status.textContent = 'Added to your bag.';
      if (addButton) addButton.textContent = 'Added';
      if (mobileAdd) mobileAdd.textContent = 'Added';
      window.setTimeout(() => {
        [addButton, buyButton, mobileAdd, mobileBuy].forEach((button) => { if (button) button.disabled = false; });
        if (addButton) addButton.textContent = 'Add to bag';
        if (mobileAdd) mobileAdd.textContent = 'Add to bag';
      }, 1200);
    } catch (e) {
      [addButton, buyButton, mobileAdd, mobileBuy].forEach((button) => { if (button) button.disabled = false; });
      if (status) status.textContent = 'We could not add this item. Please try again.';
    }
  };

  // Prevent native form submission from bypassing our feedback state.
  const form = root.querySelector('.premium-product-form');
  form?.addEventListener('submit', (e) => { e.preventDefault(); addVariant(false); });
  buyButton?.addEventListener('click', () => addVariant(true));
  mobileAdd?.addEventListener('click', () => addVariant(false));
  mobileBuy?.addEventListener('click', () => addVariant(true));

  update(true);
})();

// V42 — Shopify-synced cart quantity controls + predictive search
(() => {
  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'AUD'
  }).format(Number(cents || 0) / 100);

  const updateCartUI = (cart) => {
    const controls = [...document.querySelectorAll('[data-cart-qty]')];
    controls.forEach((control, index) => {
      const item = cart.items?.[index];
      const input = control.querySelector('input');
      if (item && input) input.value = item.quantity;
      const line = control.closest('.premium-cart-line');
      const price = line?.querySelector('[data-cart-line-price]');
      if (item && price) price.textContent = money(item.final_line_price);
    });

    const subtotal = document.querySelector('[data-cart-subtotal]');
    if (subtotal) subtotal.textContent = money(cart.total_price);

    const fallbackOriginal = (cart.items || []).reduce((sum, item) => sum + Number(item.original_line_price || item.final_line_price || 0), 0);
    const originalTotal = Number(cart.original_total_price ?? fallbackOriginal ?? cart.total_price ?? 0);
    const threshold = 10000;
    const remaining = Math.max(0, threshold - originalTotal);
    const progress = Math.min(100, (originalTotal / threshold) * 100);
    const deliveryTitle = document.querySelector('[data-cart-delivery-title]');
    const deliveryCopy = document.querySelector('[data-cart-delivery-copy]');
    const progressBar = document.querySelector('[data-cart-delivery-progress]');
    const prioritySummary = document.querySelector('[data-cart-priority-summary]');

    if (remaining > 0) {
      if (deliveryTitle) deliveryTitle.textContent = `You're ${money(remaining)} away from complimentary priority delivery.`;
      if (deliveryCopy) deliveryCopy.textContent = 'Add another piece to reach $100 order value before discounts and unlock priority delivery on us.';
      if (prioritySummary) prioritySummary.textContent = 'Calculated at checkout';
    } else {
      if (deliveryTitle) deliveryTitle.textContent = 'Complimentary priority delivery unlocked.';
      if (deliveryCopy) deliveryCopy.textContent = 'Your order qualifies based on its value before discounts.';
      if (prioritySummary) prioritySummary.textContent = 'Complimentary';
    }
    if (progressBar) progressBar.style.width = `${progress}%`;

    const badge = document.querySelector('.bag-button span');
    if (badge) {
      badge.textContent = String(cart.item_count || 0);
      badge.setAttribute('aria-label', `${cart.item_count || 0} items in bag`);
    }
  };

  const changeQuantity = async (control, quantity) => {
    const line = Number(control.dataset.line || 0);
    const input = control.querySelector('input');
    if (!line || !input) return;
    const previous = Number(input.value || 0);
    const next = Math.max(0, Number(quantity || 0));
    if (next === previous) return;

    input.value = next;
    control.classList.add('is-updating');
    control.querySelectorAll('button,input').forEach((el) => { el.disabled = true; });

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line, quantity: next })
      });
      if (!response.ok) throw new Error('Cart quantity update failed');
      const cart = await response.json();
      if (next === 0 || !cart.items?.[line - 1]) {
        window.location.reload();
        return;
      }
      updateCartUI(cart);
    } catch (error) {
      input.value = previous;
      console.error(error);
    } finally {
      control.classList.remove('is-updating');
      control.querySelectorAll('button,input').forEach((el) => { el.disabled = false; });
    }
  };

  document.querySelectorAll('[data-cart-qty]').forEach((control) => {
    const input = control.querySelector('input');
    control.querySelector('[data-qty-minus]')?.addEventListener('click', () => changeQuantity(control, Number(input.value || 0) - 1));
    control.querySelector('[data-qty-plus]')?.addEventListener('click', () => changeQuantity(control, Number(input.value || 0) + 1));
    input?.addEventListener('change', () => changeQuantity(control, Number(input.value || 0)));
  });

  const root = document.querySelector('[data-premium-search]');
  if (!root) return;
  const input = root.querySelector('[data-predictive-input]');
  const panel = root.querySelector('[data-predictive-panel]');
  const results = root.querySelector('[data-predictive-results]');
  const viewAll = root.querySelector('[data-predictive-all]');
  let timer;
  const esc = (value='') => String(value).replace(/[&<>'"]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[c]));

  const close = () => { panel.hidden = true; results.innerHTML = ''; };
  input?.addEventListener('input', () => {
    clearTimeout(timer);
    const q = input.value.trim();
    if (q.length < 2) { close(); return; }
    timer = setTimeout(async () => {
      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=5&resources[options][unavailable_products]=last`;
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Predictive search failed');
        const data = await response.json();
        const products = data?.resources?.results?.products || [];
        if (!products.length) { close(); return; }
        results.innerHTML = products.map((product) => {
          const image = product.image ? `<img src="${esc(product.image)}" alt="${esc(product.title)}" loading="lazy">` : '<span></span>';
          return `<a class="predictive-product" href="${esc(product.url)}">${image}<span class="predictive-product-title">${esc(product.title)}</span><span class="predictive-product-price">${money(product.price)}</span></a>`;
        }).join('');
        viewAll.href = `/search?q=${encodeURIComponent(q)}&type=product`;
        panel.hidden = false;
      } catch (e) { close(); }
    }, 180);
  });
  document.addEventListener('click', (e) => { if (!root.contains(e.target)) close(); });
  input?.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
})();


// V41 — launch-week countdown. When the deadline passes, promo blocks hide and the standard header message returns.
(() => {
  const promoNodes = [...document.querySelectorAll('[data-launch-offer], [data-launch-announcement]')];
  if (!promoNodes.length) return;

  const formatRemaining = (ms) => {
    const totalMinutes = Math.max(0, Math.floor(ms / 60000));
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    if (days > 0) return `Ends in ${days}d ${hours}h`;
    if (hours > 0) return `Ends in ${hours}h ${minutes}m`;
    return `Ends in ${minutes}m`;
  };

  const updateLaunchOffer = () => {
    const now = Date.now();
    promoNodes.forEach((node) => {
      const deadline = Date.parse(node.dataset.launchDeadline || '');
      if (!Number.isFinite(deadline)) return;
      if (now >= deadline) {
        if (node.matches('[data-launch-announcement]')) {
          const launch = node.querySelector('.announcement-launch');
          const fallback = node.querySelector('.announcement-fallback');
          if (launch) launch.hidden = true;
          if (fallback) fallback.hidden = false;
        } else {
          node.hidden = true;
        }
        return;
      }
      node.querySelectorAll('[data-launch-countdown]').forEach((label) => { label.textContent = formatRemaining(deadline - now); });
    });
  };

  updateLaunchOffer();
  window.setInterval(updateLaunchOffer, 60000);
})();

document.querySelectorAll('[data-copy-discount]').forEach((button) => {
  button.addEventListener('click', async () => {
    const code = button.dataset.copyDiscount || '';
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      const previous = button.textContent;
      button.textContent = 'Copied';
      window.setTimeout(() => { button.textContent = previous; }, 1400);
    } catch (e) {
      button.textContent = code;
    }
  });
});
